import { useEffect, useState } from 'react';
import Container from '@ui/container/container';
import styles from './iframe-settings.module.css';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import Text from '@ui/text/text/text';
import { StaticVideo, VideoProvider, VideoType } from '@/types/video.types';
import { GetVideo } from '@/connection/videos/get-video';
import { saveVideo } from '@/connection/videos/save-video';
import { updateVideo } from '@/connection/videos/update-video';
import toastTrigger from '@ui/toast/toast-trigger';
import OptionalComponent from '@ui/optional-component/optional-component';
import LoadingSpinner from '@ui/loading-spinner/loading-spinner';
import { useParams } from 'react-router-dom';
import { selectWorkingSession } from '@/store/slices/project/selectors';

interface IframeSettingsProps {
  isEditingInfo: boolean;
  disabled?: boolean;
}

const GetDefaultEmbedStaticVideo = (overrides: Partial<StaticVideo>): StaticVideo => ({
  type: VideoType.embed,
  name: '',
  source: '',
  uuid: '',
  channel: 0,
  created_date: undefined,
  duration_ms: 0,
  provider: VideoProvider.uploaded,
  convert_status: null,
  convert_progress: null,
  use_custom_thumbnail: false,
  original_image: null,
  status: 1,
  video_id: null,
  created_by: null,
  modified_by: null,
  ...overrides,
});

export default function IframeSettings({ disabled, isEditingInfo }: IframeSettingsProps) {
  const [video, setVideo] = useState<StaticVideo | null>(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const session = useAppSelector(selectWorkingSession);
  const defaultLanguage = session?.default_language;
  const params = useParams();
  const language = session?.streaming_options.single_stream
    ? defaultLanguage || 'en'
    : params.language || defaultLanguage || 'en';
  const dispatch = useAppDispatch();
  const sessionUuid = session?.uuid;

  useEffect(() => {
    if (!isEditingInfo || !session) {
      return;
    }

    const staticVideos = session.static_videos;
    const defaultVideoUuid = staticVideos?.[language];

    const fetchStaticVideo = async (uuid: string) => {
      setLoadingVideo(true);
      const video = await GetVideo(session.channel, uuid);
      // Only want to update the video if it's already an embed video
      if (video && video.type === VideoType.embed) {
        setVideo(video);
      } else {
        setVideo(GetDefaultEmbedStaticVideo({ channel: session.channel, base_language: language }));
      }
      setLoadingVideo(false);
    };

    if (defaultVideoUuid) {
      fetchStaticVideo(defaultVideoUuid);
    } else {
      setVideo(GetDefaultEmbedStaticVideo({ channel: session.channel, base_language: language }));
    }
  }, [isEditingInfo, session, language]);

  const updateSessionVideo = async (video: StaticVideo) => {
    if (!session || !sessionUuid || !language) return;

    try {
      // Only need to update the video becuase it's already referenced in the session
      if (video.uuid) {
        await updateVideo(video);
        return;
      }

      // Don't insert empty video
      if (!video.source) {
        return;
      }
      const savedVideo = await saveVideo(video);

      dispatch(
        saveWorkingProjectSessionByField({
          uuid: sessionUuid,
          static_videos: {
            ...(session.static_videos || {}),
            [language]: savedVideo.uuid,
          },
        })
      );
    } catch {
      toastTrigger(
        ` <div class="S1">
            <div>Unable to save iframe settings</div>
          </div>`,
        {
          type: 'error',
          autoClose: 5000,
        }
      );
    }
  };

  useEffect(() => {
    if (video && !isEditingInfo) {
      setVideo(null);
    }
  }, [isEditingInfo, video, sessionUuid, dispatch]);

  return (
    <div data-component="IframeSettings">
      <Container
        background="var(--gray-900)"
        borderRadius="12px"
        padding="12px"
        height="86px"
        className={'flex items-center justify-center'}
        inert={disabled} /* prevent elements from being focasble when not in editing mode */
      >
        <Container background="var(--gray-900)" borderRadius="12px" className={styles.textAreaContainer}>
          <OptionalComponent display={loadingVideo}>
            <LoadingSpinner />
          </OptionalComponent>

          <OptionalComponent display={!loadingVideo}>
            <textarea
              placeholder="Paste iframe"
              cols={2}
              value={video?.source || ''}
              autoCapitalize="none"
              disabled={disabled}
              className={'clear-textarea P2 padding-v-6'}
              onChange={(e) => {
                if (!isEditingInfo) return;
                setVideo((prev: StaticVideo | null) => ({
                  ...(prev || GetDefaultEmbedStaticVideo({ channel: session?.channel, base_language: language })),
                  source: e.target.value,
                }));
              }}
              onBlur={() => {
                if (video) {
                  updateSessionVideo(video);
                }
              }}
            />
            <Text tag={'p'} clamp={2} className="P2 padding-b-6 padding-t-8">
              {video?.source || 'Paste iframe'}
            </Text>
          </OptionalComponent>
        </Container>
      </Container>
    </div>
  );
}
